import type { EscalationMatrixParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { cn } from "../../lib/utils.js";
import { severities, levels, severityClass } from "./presentation.js";

export function EscalationMatrixWidget({
  data,
}: WidgetBodyProps<EscalationMatrixParams>) {
  const cells = severities.flatMap((severity) =>
    levels.flatMap((level) => {
      const cell = data.cells.find(
        (item) => item.severity === severity && item.level === level,
      );
      return cell ? [cell] : [];
    }),
  );
  return (
    <div
      role="region"
      aria-label="Escalation responses"
      // Keyboard access to the independently scrolling response list.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className="min-h-0 flex-1 overflow-auto focus-ring"
    >
      {cells.length ? (
        <ul className="divide-y divide-pb-border">
          {cells.map((cell) => (
            <li
              key={cell.severity + cell.level}
              className="grid grid-cols-[72px_minmax(0,1fr)] items-start gap-3 py-3"
            >
              <span
                className={cn(
                  "text-xs font-semibold",
                  severityClass[cell.severity],
                )}
              >
                {cell.severity}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-pb-text">
                  {cell.level}
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words text-xs text-pb-text-secondary">
                  {cell.label || "No action specified"}
                </p>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-4 text-sm text-pb-text-muted">
          No responses yet — expand to configure an escalation.
        </p>
      )}
    </div>
  );
}
